import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cardStyles from '../Card.module.css';
import { loadParentAndChildTasksOfParentTaskId, confirmAllChildren } from './parentTaskCardRedux';
import { taskIconSvg } from '../../map/constants';
import TaskBox from '../taskBox/TaskBox';
import { closeView, CONTAINERS } from '../cardNavigationComponent/cardNavigationRedux';
import { ReactComponent as EditIcon } from '../../../icons/edit.svg';
import { getSequentialWorkflowStepForTaskType } from '../../../HelperMethods/UtilityFunctions';
import {
  getChildTasksForParentTaskCard,
  getTaskByIdSelector,
  getChildTasksByParentTaskIdSelector,
} from '../../taskView/TaskViewPage/redux/reducer';
import { TaskStatus } from '../../../HelperMethods/constants';
import ShouldComponentRender from '../ShouldComponentRender';
import { ENTITY_ACCESS_TYPE, RESOURCE_TYPE, RESOURCES } from '../../../HelperMethods/role-resource-constants';
import FormPreview from '../form-preview/FormPreviewComponent';
import { loadManifestForm, clearManifestForm } from '../../taskView/TaskViewPage/ManageTaskForm/redux/actions';
import CopyComponent from '../CopyComponent';

const ParentTaskCard = React.memo(({ parentTaskId, container, tasksEditMode }) => {
  const dispatch = useDispatch();
  const [showPdfIcon, togglePdfIcon] = useState(false);
  const user = useSelector((state) => state.appReducer.loggedInUser);
  const parentTask = useSelector(getTaskByIdSelector(parentTaskId));
  const childTasks = useSelector(getChildTasksForParentTaskCard(parentTaskId));
  const childTaskFromReducer = useSelector(getChildTasksByParentTaskIdSelector(parentTaskId));
  const { loader, reload } = useSelector((state) => state.parentTaskCardReducer);
  const { manifestForm } = useSelector((state) => state.manageTaskFormReducer);
  const { loggedInUser } = useSelector((state) => state.appReducer);
  const allPicklists = useSelector((state) => state.formReducer.pickLists);
  const picklistMappings = useSelector((state) => state.picklistMappingReducer.picklistMappings);
  let { schema: manifestFormSchema, data: formData } = manifestForm;

  useEffect(() => {
    return () => {
      dispatch(clearManifestForm());
    };
  }, []);

  useEffect(() => {
    dispatch(clearManifestForm());
    togglePdfIcon(false);
    dispatch(loadParentAndChildTasksOfParentTaskId(parentTaskId, childTaskFromReducer, container));
  }, [parentTaskId]);

  useEffect(() => {
    if (reload) {
      dispatch(loadParentAndChildTasksOfParentTaskId(parentTaskId, [], container));
    }
  }, [reload]);

  useEffect(() => {
    if (!parentTask) {
      dispatch(closeView(container));
    } else {
      togglePdfIcon(false);
      dispatch(loadManifestForm(parentTask?.taskType, parentTask));
    }
  }, [parentTask]);

  useEffect(() => {
    if (manifestForm && !manifestForm.isLoading && !showPdfIcon) {
      const { schema, data } = manifestForm;
      if (manifestFormSchema?.data?.elements?.length) {
        manifestFormSchema = schema;
        formData = data;
        // formPicklist = picklistVar;
        togglePdfIcon(true);
      }
    }
  }, [manifestForm]);
  const history = useHistory();
  const sequencedChildTasks = useMemo(() => {
    if (!parentTask || !childTasks?.length) return [];
    const childTaskWorkflowStepIdMap = _.keyBy(childTasks, 'workflowStepId');
    const workflowStepIdArray = getSequentialWorkflowStepForTaskType(parentTask.taskType);
    if (!workflowStepIdArray) return childTasks;
    const sortedChildTasks = workflowStepIdArray
      .map((workflowStepId) => childTaskWorkflowStepIdMap[workflowStepId])
      .filter((t) => !!t);
    return sortedChildTasks;
  }, [parentTask, childTasks]);
  const confirmAll = useCallback(() => {
    dispatch(confirmAllChildren(parentTaskId));
  }, [parentTaskId]);
  return (
    <div className={cardStyles.card}>
      {loader ? (
        <div className="hover-loading">
          <div className="joyn2-loader" />
        </div>
      ) : (
        <>
          <div className={cardStyles.cardHeader} style={{ paddingTop: '30px' }}>
            <div className={cardStyles.taskHeaderLines}>
              <div className={cardStyles.mainHeader}>{parentTask?.taskType?.name}</div>
              <div className={cardStyles.subHeader}>Parent Task</div>
            </div>
          </div>
          <div className={cardStyles.cardContent}>
            {!user.vendorId ? (
              <div
                className={cardStyles.row}
                style={{ background: 'rgba(200, 244, 240, 0.17)', padding: '0 16px', justifyContent: 'space-between' }}
              >
                <div className={cardStyles.rowTitle}>Action</div>
                <div
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  onClick={() => {
                    history.push(`/taskViewPage/task/update/${parentTask?.id}`);
                  }}
                >
                  <EditIcon />
                  &nbsp;&nbsp;&nbsp;Edit
                </div>
              </div>
            ) : null}
            {showPdfIcon && manifestFormSchema?.data?.elements?.length && (
              <div
                className={cardStyles.row}
                style={{ background: 'rgba(200, 244, 240, 0.17)', padding: '0 16px', justifyContent: 'space-between' }}
              >
                <div className={cardStyles.rowTitle}>Print</div>
                <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <FormPreview
                      task={parentTask}
                      formData={formData}
                      originalSchema={manifestFormSchema}
                      loggedInUser={loggedInUser}
                      // picklistDict={_.keyBy(formPicklist, 'id')}
                      onlyForPrint
                      readOnly
                      allPicklistData={allPicklists}
                      picklistMappings={picklistMappings}
                      buttonStyle={{ background: 'none', padding: '4px 5px' }}
                      printLabel="Print"
                    />
                  </div>
                </div>
              </div>
            )}
            <div
              className={cardStyles.row}
              style={{ background: 'rgba(200, 244, 240, 0.17)', padding: '0 16px', justifyContent: 'space-between' }}
            >
              <div className={cardStyles.rowTitle}>Task #</div>
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                &nbsp;&nbsp;&nbsp;{parentTask?.taskNumber}&nbsp;
                <CopyComponent textToCopy={parentTask?.taskNumber} floatPosition="none" />
              </div>
            </div>
            <ChildTasks
              tasksEditMode={tasksEditMode}
              childTasks={sequencedChildTasks}
              onConfirmAll={confirmAll}
              container={container}
            />
          </div>
        </>
      )}
    </div>
  );
});

ParentTaskCard.propTypes = {
  parentTaskId: PropTypes.string.isRequired,
  tasksEditMode: PropTypes.string.isRequired,
  container: PropTypes.oneOf(Object.values(CONTAINERS)).isRequired,
};

function ChildTasks({ childTasks, container, onConfirmAll }) {
  const showConfirmButton = !!childTasks.filter((item) => item.status === TaskStatus.DRAFT).length; // && tasksEditMode === EditMode.PUBLISHED;
  return (
    <>
      <div className={cardStyles.row} style={{ background: 'rgba(0, 0, 0, 0.034)', position: 'relative' }}>
        <div className={cardStyles.rowIcon}>
          <img
            alt="task-icon"
            src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(taskIconSvg)}`}
            style={{ height: '12px' }}
          />
        </div>
        <div className={cardStyles.rowTitle}>
          <span>Child Tasks</span>
          {showConfirmButton && (
            <ShouldComponentRender
              resource={RESOURCES.ENTITY.TASK}
              resourceType={RESOURCE_TYPE.ENTITY}
              entityAccessType={ENTITY_ACCESS_TYPE.UPDATE}
            >
              <Button
                type="button"
                className="black"
                style={{
                  display: 'inline-block',
                  padding: '7px 10px',
                  position: 'absolute',
                  right: '8px',
                  top: '8px',
                  fontSize: '12px',
                }}
                onClick={() => onConfirmAll()}
              >
                Confirm All
              </Button>
            </ShouldComponentRender>
          )}
        </div>
      </div>
      {childTasks.map((task) => (
        <TaskBox key={task.id} task={task} container={container} />
      ))}
    </>
  );
}

ChildTasks.propTypes = {
  childTasks: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })).isRequired,
  container: PropTypes.oneOf(Object.values(CONTAINERS)).isRequired,
  onConfirmAll: PropTypes.func.isRequired,
  // tasksEditMode: PropTypes.string.isRequired,
};

export default ParentTaskCard;
